/**
 * The prop catalogue and the collision maths — what a piece of scenery *is*, and
 * how a tank and a shell interact with one.
 *
 * Where each one *stands* is a map, and maps live in `maps.ts`. The split is
 * one-directional on purpose: `maps.ts` imports this, this imports nothing of
 * `maps.ts`. A catalogue that knew about maps and maps that knew about the
 * catalogue would be a cycle whose module-initialisation order decides whether
 * `MAP_TOWN` is built before the definitions it reads — the kind of bug that
 * appears only in one bundler.
 *
 * Shared, like the simulation, and for the same reason — the client predicts its
 * own movement, so it has to resolve a collision against the same wall, in the
 * same place, to the same millimetre. A prop the server knows about and the
 * client does not is a tank that visibly slides into a building and is yanked
 * back out.
 *
 * Everything here is **pure and static**. Props never move, never tick and are
 * never sent over the wire per frame: a map is identified by name at join and
 * both sides look the definition up in this file. That is what keeps the cost of
 * a hundred buildings at zero bytes per snapshot.
 *
 * Collision shapes are boxes, not meshes. A tank is a circle of
 * `TANK_HIT_RADIUS`, a building is an oriented box, and the whole thing is a few
 * lines of arithmetic that both sides run identically. Nobody has ever wanted a
 * tank to fit into an alcove precisely enough to justify a mesh collider.
 */

/** Identifies a kind of prop. The catalogue below owns them. */
export type PropId = string;

/**
 * What a prop is, independent of where it is standing.
 *
 * Footprints are half-extents on the ground plane, in metres, measured before
 * the placement yaw is applied — so a fence section is long in X and thin in Z,
 * and standing it across a road is a placement, not a different prop.
 */
export interface PropDefinition {
  id: PropId;
  /** Shown in the map editor and in nothing else. */
  name: string;
  /**
   * Path to the mesh, or `null` while the art is still being made.
   *
   * `null` is a working state, not a broken one: the renderer draws a plain
   * block of exactly the collision size, which is honest about what the
   * simulation thinks is there and is genuinely useful for laying a map out
   * before any of it has been modelled. Same shape as the tank models before
   * their GLBs landed.
   */
  model: string | null;
  /**
   * Yaw correction bringing the mesh's own idea of forward onto +Z, in radians.
   *
   * Per model, exactly as for hulls and turrets — image-to-3D gives every mesh
   * its own facing. See `docs/model-pipeline.md`.
   */
  modelYaw: number;
  /** Half the footprint along the prop's local X, in metres. */
  halfX: number;
  /** Half the footprint along the prop's local Z, in metres. */
  halfZ: number;
  /** Height in metres. Used by the renderer and by shell clearance. */
  height: number;
  /**
   * Uniform scale applied to the mesh, taking it from unit-normalised to metres.
   *
   * Per prop rather than one constant, because image-to-3D normalises the
   * *longest* axis — which is height on a water tower and length on a warehouse,
   * so a single divisor would make one of them absurd. Derived from the mesh's
   * own measured extents against one chosen dimension; the footprint above is
   * then what the mesh actually occupies at this scale, which is what keeps the
   * collision box and the thing the player sees the same object.
   */
  modelScale: number;
  /**
   * Whether a shell is stopped by it.
   *
   * Separate from blocking tanks because the two genuinely differ. Nothing in
   * the catalogue currently states the two apart — every prop either blocks
   * both or neither — but `clip-masks.ts`'s `SHELL_BLOCKING_MASK` splits them
   * at placement time: a Hesco barrier's mask stops a tank while the barrier
   * itself still stops the round.
   * **The reverse pairing is the one the catalogue refuses**: a prop that stops
   * a tank always stops a shell, because a box you cannot drive through but can
   * shoot through reads as a bug rather than as a rule, and `props.test.ts`
   * fails for it.
   *
   * That rule is about geometry a player can *see*, so its one exception is the
   * `invisible: true` clip volume `clip_wall_move` — there is no drawn box to
   * contradict, and an invisible shell-stopper would be the less legible of the
   * two (a round dying in mid-air over open ground). The exception is a list of
   * ids in `props.test.ts`, not a blanket exemption for `invisible`, so a second
   * one has to be a deliberate test diff.
   */
  blocksShells: boolean;
  /**
   * Whether a tank is stopped by it.
   *
   * **`true` is the default and the overwhelming majority.** It used to be a
   * genuine per-prop judgement — forty-three of the hundred definitions stopped
   * nothing, including every wall section, fence, wreck and sandbag line — and
   * the effect in play was that a tank drove through most of the scenery it was
   * pointed at. Solid is now the rule and `false` is a stated exception, which
   * is why `prop()` defaults it true: a prop added without thinking about this
   * blocks, and the failure mode of forgetting is a map that is too tight rather
   * than scenery that is not there.
   *
   * **The exceptions are exactly two families, and `PASS_THROUGH` enumerates
   * them.** *Small* vegetation — hedges, bushes, grass, an orchard tree, a
   * coppice — because driving through underbrush is what a tank does to it;
   * and ground features — the shell crater, the stump, the fallen log, the
   * loading ramp — because they are things you drive *over*, and the
   * simulation has no verticality to express that with. Anything built, stacked
   * or abandoned is solid.
   *
   * **A big standing tree is solid at the trunk, not the canopy**, and it left
   * both families on 2026-08-19. The trees went through three states: solid at
   * the canopy (61 of 89 props were hard cover and a wood could only be a wall),
   * then drive-through entirely — which shipped the reported bug of a tank
   * sailing through a whole oak, trunk and all, reading as broken collision
   * rather than as driving under foliage. Their footprints are the mesh's own XZ
   * extents **between 0.4 m and 2.5 m up** — the geometry a hull actually meets,
   * with the band starting above 0 to exclude each mesh's drive-over ground
   * plate. The canopy deliberately overhangs the box (the `utility_pole`
   * precedent, named in `prop-fit`'s `DELIBERATE_OVERHANG`), so a tank noses
   * under the outer foliage and stops on the wood.
   *
   * Still drive-through: the small stuff — `tree_orchard`, whose canopy sits at
   * hull height so a solid box would make every orchard a wall, and
   * `coppice_stand`, a ground-plate stand whose hull-height extent is its whole
   * 44 m² footprint.
   */
  blocksTanks: boolean;
  /**
   * `true` for a collision-only volume the renderer must never draw — not
   * even the `model === null` placeholder block.
   *
   * This is a different state from `model === null`, and the two must not be
   * conflated. `model: null` is *waiting for art* — the placeholder is an
   * honest stand-in for a body that will eventually get a mesh, and drawing
   * one is the rule precisely because "an invisible wall is far worse than
   * an ugly one" (see `model`'s own doc). `invisible: true` is the opposite
   * intent: a level designer, in the map editor, deliberately placing a
   * collision box that is never meant to be seen — a lane a tank should not
   * enter, a base's fourth wall, a gap sealed without a building to justify
   * it. It will never grow a mesh, so there is nothing to be "waiting" for.
   *
   * **Both clip volumes block tanks, and that is structural rather than
   * incidental.** The pairing the catalogue must never offer on an invisible box
   * is `blocksTanks: false, blocksShells: true` because a tank could park inside it, its own rounds would leave
   * (`firstBlockingProp` exempts the prop a shell is born in) and every incoming
   * round would die on a box neither player can see. That is the "unhittable
   * spot" `props.test.ts` polices, and the reported *"I definitely hit him and he
   * takes no damage"*. Since both entries block tanks, no tank can ever be inside
   * one, so the pathology is unreachable rather than merely unlikely. The editor
   * refuses to arm the combination.
   *
   * Defaults to `false` via `prop()`, so nothing existing has to state it.
   */
  invisible?: boolean;
}

/** A prop standing somewhere on a map. */
export interface PlacedProp {
  propId: PropId;
  x: number;
  z: number;
  /** Rotation about Y, radians. Applied on top of the definition's `modelYaw`. */
  yaw: number;
  /**
   * Metres above the ground plane to draw this placement at. Absent is 0.
   *
   * ── Visual only, and that is a decision rather than a limitation ────────────
   *
   * Nothing in the simulation reads it. `resolveCircle` works on the ground
   * plane — a tank's collision against a prop is a 2-D question and `height`
   * on a `PropDefinition` is already only used for drawing and for line-of-
   * sight, never for stepping over anything. So a prop lifted three metres is
   * still solid at ground level, and one sunk into the floor still stops a
   * tank at its full footprint.
   *
   * Making it collide would mean a Z axis in `resolveCircle`, in every prop
   * footprint, in the shell path, and — because the client predicts against
   * the identical code — in the smoothness contract. That is a project, not a
   * field. Until somebody takes it on, an author raising a prop is dressing a
   * scene, not building a bridge.
   *
   * ── Why it lives here now ───────────────────────────────────────────────────
   *
   * It used to be an intersection type in the map editor
   * (`HangarPlacedProp = PlacedProp & { liftY?: number }`) on the argument that
   * only the garage's yard needed it, and `PropScene` recovered it by matching
   * a resolved body back to its source on `propId`, `x` and `z`.
   *
   * That matching is wrong wherever two identical props share a spot, which is
   * exactly what a height field invites: the garage's yard stacks six
   * `rusty_pillar` at `[21, 10]` at 2, 4, 6, 8, 10 and 12 metres, and a `find`
   * on three fields returns the first for all six.
   *
   * **Where that bit was the map editor's preview, not the garage.** The
   * garage draws its yard through `hangar-room.ts`'s `yardPlacements()`, which
   * reads `spec.liftY` off each spec directly and was always right; the
   * editor's live preview builds a `PropScene` from the same placements and so
   * drew the stack as one pillar at 2 m. Carried on the body now, from the
   * placement, by index rather than by value — so the two agree by
   * construction instead of by coincidence.
   */
  liftY?: number;
  /**
   * Absolute half-extents and height overriding the catalogue's own, in
   * metres — the one per-placement override this codebase carries, and it
   * exists for exactly one family.
   *
   * A footprint is normally a contract with a mesh (see `modelScale`'s own
   * argument, and "a footprint is a contract" in the design notes): the art
   * is scaled to fit the collision box, so a per-placement override would
   * silently open a gap between what a player sees and what the simulation
   * resolves against — the exact failure the catalogue-variant approach
   * (`clip_wall` vs `clip_wall_move`) exists to avoid. An invisible clip
   * volume has no mesh to disagree with — `PropDefinition.invisible` means
   * nothing is ever drawn for it, in the arena or in the map editor's live
   * preview — so there is nothing on screen a resized box could contradict.
   * That is what makes an override safe for this one family and unsafe for
   * every other: `buildBodies` (in `maps.ts`) reads this field only when the
   * definition is `invisible`, so a stray `clipSize` on an ordinary, meshed
   * prop is silently ignored rather than trusted.
   *
   * Set by the map editor's clip-zone size fields; absent means the
   * catalogue's own `halfX`/`halfZ`/`height` for the `propId`, exactly as
   * every clip zone behaved before this field existed.
   */
  clipSize?: { halfX: number; halfZ: number; height: number };
  /**
   * Per-placement overrides of the catalogue's own `blocksTanks` /
   * `blocksShells`, for the two cases a level designer cannot express by
   * choosing a different prop: a **noclip** dressing piece you drive straight
   * through, and a **low** piece you shoot straight over.
   *
   * Absent means the catalogue's value, so every placement written before this
   * existed keeps behaving exactly as it did, and a map file re-baked without
   * touching these is byte-identical.
   *
   * **This is a deliberate relaxation, at the placement level only, of the rule
   * `blocksTanks` implies `blocksShells`** — the rule the catalogue states in
   * `blocksShells`' own doc, on the grounds that a box you cannot drive through
   * but can shoot through reads as a bug rather than as a rule. That argument is
   * about a *prop type*, where the shape is whatever the mesh happens to be. It
   * does not carry to a placement a designer picked deliberately: a knee-high
   * sandbag line you can fire over is a rule players read correctly off the
   * geometry, and it was asked for repeatedly. `props.test.ts` still holds the
   * **catalogue** to the rule, so a definition cannot drift into this quietly —
   * only a placement can, and only where somebody typed it.
   *
   * The one pairing that stays refused is the dangerous one, and only where it
   * is dangerous — see `placementBlocking`.
   */
  blocksTanks?: boolean;
  blocksShells?: boolean;
}

/**
 * What a placement actually blocks: its overrides over the catalogue's values.
 *
 * The single place the two fields above are interpreted, so the simulation, the
 * shell path, the bots and the map editor cannot disagree about what a
 * placement does.
 *
 * **`blocksShells` without `blocksTanks` is refused on an invisible body, and
 * nowhere else.** That pairing is the "free cover" bug: a box that stops rounds
 * while letting tanks in gives the tank parked inside it total immunity —
 * `firstBlockingProp` exempts the prop a shell is *born* in, so its own fire
 * leaves normally while every incoming round dies on something neither player
 * can see. On a *visible* prop the same pairing is an ordinary hedgerow and is
 * exactly what the catalogue already ships, because there is drawn geometry to
 * explain where the round went. A refused override falls back to the
 * catalogue's own pair rather than throwing, the way a stray `clipSize` on a
 * meshed prop is ignored: a map is data, and a bad field in it should cost one
 * flag, not the match.
 */
export function placementBlocking(
  placed: Pick<PlacedProp, 'blocksTanks' | 'blocksShells'>,
  def: Pick<PropDefinition, 'invisible' | 'blocksTanks' | 'blocksShells'>,
): { blocksTanks: boolean; blocksShells: boolean } {
  const blocksTanks = placed.blocksTanks ?? def.blocksTanks;
  const blocksShells = placed.blocksShells ?? def.blocksShells;
  if (def.invisible && blocksShells && !blocksTanks) {
    return { blocksTanks: def.blocksTanks, blocksShells: def.blocksShells };
  }
  return { blocksTanks, blocksShells };
}

/**
 * A prop resolved for collision: world position, world rotation and extents.
 *
 * Built once when a map is loaded rather than looked up per test, because the
 * inner loop runs for every tank against every nearby prop, thirty times a
 * second, on both sides.
 */
export interface PropBody {
  propId: PropId;
  x: number;
  z: number;
  yaw: number;
  halfX: number;
  halfZ: number;
  height: number;
  blocksShells: boolean;
  blocksTanks: boolean;
  /**
   * Metres above the ground plane the art is drawn at. Absent is 0.
   *
   * Carried through from `PlacedProp.liftY` so a renderer reads it off the body
   * it is already holding. **The simulation ignores it** — see the placement
   * field's own note for why height is not collision here.
   */
  liftY?: number;
  /** Radius of the circle enclosing the footprint — the cheap rejection test. */
  boundingRadius: number;
}

// ─── The catalogue ───────────────────────────────────────────────────────────
//
// Footprints are **derived from the meshes**, not chosen independently. Each
// model was measured, one dimension was picked as the target size in metres —
// height for a tower, length for a warehouse — and the remaining extents fall
// out of the same scale. That is what keeps the collision box and the building
// the player sees the same object; a footprint invented separately would leave
// tanks stopping short of walls or clipping into them, and no amount of care in
// the geometry can rescue a box that is not where the mesh is.
//
// Re-derive with `scratchpad/gen-props.mjs` if a mesh is ever re-exported.

const prop = (over: Partial<PropDefinition> & Pick<PropDefinition, 'id' | 'name'>): PropDefinition => ({
  model: null,
  modelYaw: 0,
  halfX: 4,
  halfZ: 4,
  height: 6,
  modelScale: 1,
  blocksShells: true,
  blocksTanks: true,
  invisible: false,
  ...over,
});

/** Where the building meshes live. */
const MESH = (name: string) => `/assets/props/${name}.glb`;

export const PROP_DEFINITIONS: Record<PropId, PropDefinition> = {
  // ── Buildings ──
  house_brick_small: prop({
    id: 'house_brick_small', name: 'Brick house',
    halfX: 2.79, halfZ: 2.66, height: 7, modelScale: 7.14, modelYaw: 0,
    model: MESH('house-brick-small'),
  }),
  house_two_storey: prop({
    id: 'house_two_storey', name: 'Suburban house',
    halfX: 4.46, halfZ: 3.44, height: 8, modelScale: 9.11, modelYaw: Math.PI / 2,
    model: MESH('house-two-storey'),
  }),
  apartment_block: prop({
    id: 'apartment_block', name: 'Apartment block',
    halfX: 5.57, halfZ: 4.34, height: 15, modelScale: 15.32, modelYaw: Math.PI / 2,
    model: MESH('apartment-block'),
  }),
  warehouse_brick: prop({
    id: 'warehouse_brick', name: 'Warehouse',
    halfX: 10, halfZ: 6.42, height: 8.13, modelScale: 20.43, modelYaw: 0,
    model: MESH('warehouse-brick'),
  }),
  barn_wooden: prop({
    id: 'barn_wooden', name: 'Barn',
    halfX: 5.5, halfZ: 4.68, height: 9, modelScale: 11.26, modelYaw: 0,
    model: MESH('barn-wooden'),
  }),
  chapel_stone: prop({
    id: 'chapel_stone', name: 'Chapel',
    halfX: 3.57, halfZ: 3.48, height: 11, modelScale: 11.26, modelYaw: 0,
    model: MESH('chapel-stone'),
  }),
  shed_corrugated: prop({
    id: 'shed_corrugated', name: 'Shed',
    halfX: 2.4, halfZ: 2.08, height: 4, modelScale: 4.9, modelYaw: 0,
    model: MESH('shed-corrugated'),
  }),
  ruin_brick_shell: prop({
    id: 'ruin_brick_shell', name: 'Ruined house',
    halfX: 3.51, halfZ: 3.09, height: 6, modelScale: 7.16, modelYaw: Math.PI / 2,
    model: MESH('ruin-brick-shell'),
  }),
  bunker_concrete: prop({
    id: 'bunker_concrete', name: 'Bunker',
    halfX: 3.13, halfZ: 3.03, height: 3.5, modelScale: 6.4, modelYaw: Math.PI / 2,
    model: MESH('bunker-concrete'),
  }),
  guard_tower: prop({
    id: 'guard_tower', name: 'Guard tower',
    halfX: 4.31, halfZ: 3.02, height: 10, modelScale: 10.2, modelYaw: 0,
    model: MESH('guard-tower'),
  }),
  water_tower: prop({
    id: 'water_tower', name: 'Water tower',
    halfX: 2.59, halfZ: 2.59, height: 14, modelScale: 14.29, modelYaw: 0,
    model: MESH('water-tower'),
  }),
  fuel_silo: prop({
    id: 'fuel_silo', name: 'Fuel silo',
    halfX: 5.39, halfZ: 5.08, height: 9, modelScale: 11, modelYaw: 0,
    model: MESH('fuel-silo'),
  }),

  // ── Landscape and industry ──
  rock_outcrop: prop({
    id: 'rock_outcrop', name: 'Rock outcrop',
    halfX: 5.5, halfZ: 4, height: 7, modelScale: 10.8, modelYaw: Math.PI / 2,
    model: MESH('rock-outcrop'),
  }),
  rubble_pile: prop({
    id: 'rubble_pile', name: 'Rubble mound',
    halfX: 2.65, halfZ: 2.6, height: 2.4, modelScale: 5.4, modelYaw: 0,
    model: MESH('rubble-pile'),
  }),
  pipe_rack: prop({
    id: 'pipe_rack', name: 'Pipe rack',
    halfX: 5.9, halfZ: 2.2, height: 4.1, modelScale: 12, modelYaw: 0,
    model: MESH('pipe-rack'),
  }),
  crane_gantry: prop({
    id: 'crane_gantry', name: 'Industrial machine',
    halfX: 2.8, halfZ: 4, height: 7.2, modelScale: 8.16, modelYaw: 0,
    model: MESH('crane-gantry'),
  }),

  // ── Field fortifications ──
  command_post: prop({
    id: 'command_post', name: 'Command post',
    halfX: 4.2, halfZ: 3.4, height: 5.6, modelScale: 8.2, modelYaw: Math.PI / 2,
    model: MESH('command-post'),
  }),
  gate_pylon: prop({
    id: 'gate_pylon', name: 'Gate pylon',
    halfX: 1.6, halfZ: 1.6, height: 5.25, modelScale: 5.33, modelYaw: 0,
    model: MESH('gate-pylon'),
  }),

  // ── Barriers. A section's `halfX` is its spacing — CLAUDE.assets.md ──
  hesco_barrier: prop({
    id: 'hesco_barrier', name: 'Hesco barrier',
    halfX: 2.5, halfZ: 1.05, height: 2.25, modelScale: 5.1, modelYaw: Math.PI / 2,
    model: MESH('hesco-barrier'),
  }),
  dragon_teeth: prop({
    id: 'dragon_teeth', name: 'Dragon’s teeth',
    // A round could pass between two teeth; the catalogue refuses that pairing.
    halfX: 2.4, halfZ: 0.65, height: 1.05, modelScale: 4.9, modelYaw: Math.PI / 2,
    model: MESH('dragon-teeth'),
  }),
  hedgerow: prop({
    id: 'hedgerow', name: 'Hedgerow',
    // Drive through it, shoot through it — same as a bush.
    halfX: 3, halfZ: 1, height: 2.15, modelScale: 6.12, modelYaw: Math.PI / 2,
    model: MESH('hedgerow'),
    blocksTanks: false, blocksShells: false,
  }),
  fence_wood: prop({
    id: 'fence_wood', name: 'Wooden fence',
    halfX: 1.9, halfZ: 0.19, height: 2, modelScale: 3.88, modelYaw: Math.PI / 2,
    model: MESH('fence-wood'),
  }),
  wall_concrete: prop({
    id: 'wall_concrete', name: 'Concrete wall',
    halfX: 2.78, halfZ: 0.24, height: 2.6, modelScale: 5.66, modelYaw: Math.PI / 2,
    model: MESH('wall-concrete'),
  }),
  sandbag_wall: prop({
    id: 'sandbag_wall', name: 'Sandbag wall',
    halfX: 1.72, halfZ: 0.7, height: 1.2, modelScale: 3.51, modelYaw: Math.PI / 2,
    model: MESH('sandbag-wall'),
  }),

  // ── Vegetation ──
  tree_oak: prop({
    id: 'tree_oak', name: 'Oak',
    halfX: 2.62, halfZ: 2.69, height: 11, modelScale: 11.25, modelYaw: Math.PI / 2,
    model: MESH('tree-oak'),
  }),
  tree_pine: prop({
    id: 'tree_pine', name: 'Pine',
    halfX: 1.36, halfZ: 1.42, height: 14, modelScale: 14.29, modelYaw: 0,
    model: MESH('tree-pine'),
  }),
  tree_poplar: prop({
    id: 'tree_poplar', name: 'Poplar',
    halfX: 1.89, halfZ: 1.81, height: 15, modelScale: 15.29, modelYaw: Math.PI / 2,
    model: MESH('tree-poplar'),
  }),
  tree_birch_clump: prop({
    id: 'tree_birch_clump', name: 'Birch clump',
    halfX: 2.15, halfZ: 2.56, height: 10, modelScale: 10.21, modelYaw: Math.PI / 2,
    model: MESH('tree-birch-clump'),
  }),
  tree_willow: prop({
    id: 'tree_willow', name: 'Willow',
    halfX: 3.41, halfZ: 1.41, height: 8, modelScale: 8.19, modelYaw: Math.PI / 2,
    model: MESH('tree-willow'),
  }),
  tree_dead: prop({
    id: 'tree_dead', name: 'Dead tree',
    halfX: 2.6, halfZ: 2.14, height: 9, modelScale: 9.17, modelYaw: Math.PI / 2,
    model: MESH('tree-dead'),
  }),
  tree_burnt: prop({
    id: 'tree_burnt', name: 'Burnt snag',
    halfX: 1.79, halfZ: 1.78, height: 5, modelScale: 5.1, modelYaw: 0,
    model: MESH('tree-burnt'),
  }),
  tree_fallen: prop({
    id: 'tree_fallen', name: 'Fallen tree',
    halfX: 2.84, halfZ: 1.6, height: 2.55, modelScale: 5.77, modelYaw: Math.PI / 2,
    model: MESH('tree-fallen'),
  }),
  tree_orchard: prop({
    id: 'tree_orchard', name: 'Apple tree',
    // Canopy at hull height.
    halfX: 2.82, halfZ: 2.2, height: 5, modelScale: 5.76, modelYaw: Math.PI / 2,
    blocksTanks: false,
    blocksShells: false,
    model: MESH('tree-orchard'),
  }),
  tree_oak_ancient: prop({
    id: 'tree_oak_ancient', name: 'Ancient oak',
    halfX: 5.58, halfZ: 5.6, height: 15, modelScale: 15.31, modelYaw: Math.PI / 2,
    model: MESH('tree-oak-ancient'),
  }),
  bush_round: prop({
    id: 'bush_round', name: 'Bush',
    // Stops nothing.
    halfX: 1.22, halfZ: 1.2, height: 2, modelScale: 2.48, modelYaw: Math.PI / 2,
    model: MESH('bush-round'),
    blocksTanks: false, blocksShells: false,
  }),
  bush_bramble: prop({
    id: 'bush_bramble', name: 'Bramble thicket',
    // Stops nothing.
    halfX: 1.73, halfZ: 1.53, height: 1.4, modelScale: 3.53, modelYaw: Math.PI / 2,
    model: MESH('bush-bramble'),
    blocksTanks: false, blocksShells: false,
  }),
  hedgerow_wild: prop({
    id: 'hedgerow_wild', name: 'Overgrown hedgerow',
    // Section. Same pairing as `hedgerow`.
    halfX: 3, halfZ: 0.88, height: 2.83, modelScale: 5.98, modelYaw: 0,
    model: MESH('hedgerow-wild'),
    blocksTanks: false, blocksShells: false,
  }),
  stump_cut: prop({
    id: 'stump_cut', name: 'Tree stump',
    // Driven over.
    halfX: 0.97, halfZ: 0.9, height: 1, modelScale: 1.96, modelYaw: Math.PI / 2,
    model: MESH('stump-cut'),
    blocksTanks: false, blocksShells: false,
  }),
  log_fallen: prop({
    id: 'log_fallen', name: 'Fallen log',
    // Driven over.
    halfX: 1.39, halfZ: 0.7, height: 1.29, modelScale: 2.83, modelYaw: Math.PI / 2,
    model: MESH('log-fallen'),
    blocksTanks: false, blocksShells: false,
  }),
  grass_tall: prop({
    id: 'grass_tall', name: 'Tall grass',
    // Stops nothing.
    halfX: 0.68, halfZ: 0.67, height: 1.2, modelScale: 1.38, modelYaw: Math.PI / 2,
    model: MESH('grass-tall'),
    blocksTanks: false, blocksShells: false,
  }),

  // ── Buildings and ruins ──
  cottage_stone: prop({
    id: 'cottage_stone', name: 'Stone cottage',
    halfX: 4.04, halfZ: 3.4, height: 8, modelScale: 8.24, modelYaw: Math.PI / 2,
    model: MESH('cottage-stone'),
  }),
  farmhouse_long: prop({
    id: 'farmhouse_long', name: 'Farmhouse',
    halfX: 7, halfZ: 3.04, height: 8.09, modelScale: 14.31, modelYaw: Math.PI / 2,
    model: MESH('farmhouse-long'),
  }),
  terrace_row: prop({
    id: 'terrace_row', name: 'Terrace row',
    halfX: 7.56, halfZ: 4, height: 12.36, modelScale: 15.44, modelYaw: Math.PI / 2,
    model: MESH('terrace-row'),
  }),
  industrial_unit: prop({
    id: 'industrial_unit', name: 'Industrial unit',
    halfX: 9.21, halfZ: 7, height: 11.26, modelScale: 18.79, modelYaw: 0,
    model: MESH('industrial-unit'),
  }),
  factory_hall: prop({
    id: 'factory_hall', name: 'Factory hall',
    halfX: 12, halfZ: 6.62, height: 11.31, modelScale: 24.51, modelYaw: 0,
    model: MESH('factory-hall'),
  }),
  workshop_garage: prop({
    id: 'workshop_garage', name: 'Workshop garage',
    halfX: 4.78, halfZ: 4.5, height: 6.32, modelScale: 9.73, modelYaw: Math.PI / 2,
    model: MESH('workshop-garage'),
  }),
  civic_hall: prop({
    id: 'civic_hall', name: 'Civic hall',
    halfX: 7.25, halfZ: 5.5, height: 14.37, modelScale: 14.82, modelYaw: Math.PI / 2,
    model: MESH('civic-hall'),
  }),
  school_block: prop({
    id: 'school_block', name: 'School block',
    halfX: 6.63, halfZ: 4.5, height: 10.25, modelScale: 13.51, modelYaw: Math.PI / 2,
    model: MESH('school-block'),
  }),
  grain_store: prop({
    id: 'grain_store', name: 'Grain store',
    halfX: 4, halfZ: 3.11, height: 12.38, modelScale: 12.66, modelYaw: 0,
    model: MESH('grain-store'),
  }),
  boiler_house: prop({
    id: 'boiler_house', name: 'Boiler house',
    halfX: 6.57, halfZ: 6.17, height: 16, modelScale: 16.34, modelYaw: Math.PI / 2,
    model: MESH('boiler-house'),
  }),
  brick_smokestack: prop({
    id: 'brick_smokestack', name: 'Brick smokestack',
    // Height-anchored, not footprint-anchored — the mesh's own proportions
    // (squat, ~2:1 height:width) stay honest rather than being distorted to
    // fit an assumed footprint. 20 m (2.5x the original 8 m, by request) is
    // now above boiler_house's 16 m — the tallest thing in the game per
    // CLAUDE.game.md — so this reads as the taller landmark on any map that
    // places both.
    halfX: 4.33, halfZ: 5.58, height: 20, modelScale: 20, modelYaw: 0,
    model: MESH('brick-smokestack'),
  }),
  depot_control: prop({
    id: 'depot_control', name: 'Depot control',
    halfX: 4.18, halfZ: 4, height: 7.75, modelScale: 8.52, modelYaw: Math.PI / 2,
    model: MESH('depot-control'),
  }),
  brick_outbuilding: prop({
    id: 'brick_outbuilding', name: 'Brick outbuilding',
    halfX: 2.25, halfZ: 2.2, height: 4.7, modelScale: 4.8, modelYaw: 0,
    model: MESH('brick-outbuilding'),
  }),
  ruin_roofless: prop({
    id: 'ruin_roofless', name: 'Ruin, roof gone',
    halfX: 3.8, halfZ: 3.09, height: 5.37, modelScale: 7.91, modelYaw: Math.PI / 2,
    model: MESH('ruin-roofless'),
  }),
  ruin_gable: prop({
    id: 'ruin_gable', name: 'Ruin, one gable',
    halfX: 2.46, halfZ: 2.4, height: 6.35, modelScale: 6.46, modelYaw: Math.PI / 2,
    model: MESH('ruin-gable'),
  }),
  ruin_corner: prop({
    id: 'ruin_corner', name: 'Ruined corner',
    halfX: 3, halfZ: 3, height: 7.22, modelScale: 7.36, modelYaw: 0,
    model: MESH('ruin-corner'),
  }),
  ruin_warehouse: prop({
    id: 'ruin_warehouse', name: 'Ruined hall',
    halfX: 6.73, halfZ: 6, height: 8.1, modelScale: 13.76, modelYaw: Math.PI / 2,
    model: MESH('ruin-warehouse'),
  }),
  ruin_apartment: prop({
    id: 'ruin_apartment', name: 'Ruined apartment block',
    halfX: 5.57, halfZ: 3.9, height: 11.2, modelScale: 11.39, modelYaw: Math.PI / 2,
    model: MESH('ruin-apartment'),
  }),
  ruin_church: prop({
    id: 'ruin_church', name: 'Ruined church',
    halfX: 3.57, halfZ: 2.01, height: 7.07, modelScale: 7.3, modelYaw: Math.PI / 2,
    model: MESH('ruin-church'),
  }),

  // ── Fences, barriers and gates ──
  fence_chainlink: prop({
    id: 'fence_chainlink', name: 'Chain-link fence',
    halfX: 2.5, halfZ: 0.49, height: 3.43, modelScale: 5.1, modelYaw: Math.PI / 2,
    model: MESH('fence-chainlink'),
  }),
  wall_drystone: prop({
    id: 'wall_drystone', name: 'Drystone wall',
    halfX: 2.4, halfZ: 0.86, height: 2, modelScale: 4.38, modelYaw: Math.PI / 2,
    model: MESH('wall-drystone'),
  }),
  wall_brick_garden: prop({
    id: 'wall_brick_garden', name: 'Brick garden wall',
    halfX: 2.6, halfZ: 0.45, height: 2.71, modelScale: 5.34, modelYaw: Math.PI / 2,
    model: MESH('wall-brick-garden'),
  }),
  hoarding_corrugated: prop({
    id: 'hoarding_corrugated', name: 'Corrugated hoarding',
    halfX: 2.5, halfZ: 0.61, height: 3.48, modelScale: 5.18, modelYaw: Math.PI / 2,
    model: MESH('hoarding-corrugated'),
  }),
  barrier_jersey: prop({
    id: 'barrier_jersey', name: 'Jersey barrier',
    halfX: 1.5, halfZ: 0.71, height: 1.94, modelScale: 3.07, modelYaw: 0,
    model: MESH('barrier-jersey'),
  }),
  concrete_barrier: prop({
    id: 'concrete_barrier', name: 'Concrete barrier',
    halfX: 0.47, halfZ: 1, height: 1.29, modelScale: 2.05, modelYaw: 0,
    model: MESH('concrete-barrier'),
  }),
  hedgehog_antitank: prop({
    id: 'hedgehog_antitank', name: 'Anti-tank hedgehog',
    halfX: 1.6, halfZ: 1.46, height: 3.99, modelScale: 4.07, modelYaw: Math.PI / 2,
    model: MESH('hedgehog-antitank'),
  }),
  berm_earth: prop({
    id: 'berm_earth', name: 'Earth berm',
    halfX: 3, halfZ: 2.06, height: 3.12, modelScale: 6.12, modelYaw: Math.PI / 2,
    model: MESH('berm-earth'),
  }),
  sandbag_pit: prop({
    id: 'sandbag_pit', name: 'Sandbag emplacement',
    halfX: 2.4, halfZ: 2.4, height: 1.64, modelScale: 4.9, modelYaw: 0,
    model: MESH('sandbag-pit'),
  }),
  gate_farm: prop({
    id: 'gate_farm', name: 'Farm gate',
    halfX: 2.2, halfZ: 0.59, height: 2.58, modelScale: 4.49, modelYaw: Math.PI / 2,
    model: MESH('gate-farm'),
  }),
  gate_steel: prop({
    id: 'gate_steel', name: 'Steel gate',
    halfX: 3, halfZ: 0.79, height: 3.67, modelScale: 6.12, modelYaw: Math.PI / 2,
    model: MESH('gate-steel'),
  }),

  // ── Ground features ──
  crater_small: prop({
    id: 'crater_small', name: 'Shell crater',
    // Driven over.
    halfX: 2.6, halfZ: 2.44, height: 0.82, modelScale: 5.31, modelYaw: Math.PI / 2,
    model: MESH('crater-small'),
    blocksTanks: false, blocksShells: false,
  }),
  spoil_heap: prop({
    id: 'spoil_heap', name: 'Spoil heap',
    halfX: 3.22, halfZ: 3.2, height: 2.52, modelScale: 6.54, modelYaw: Math.PI / 2,
    model: MESH('spoil-heap'),
  }),
  rubble_bank: prop({
    id: 'rubble_bank', name: 'Rubble bank',
    halfX: 3, halfZ: 1.4, height: 1.59, modelScale: 6.13, modelYaw: 0,
    model: MESH('rubble-bank'),
  }),
  loading_ramp: prop({
    id: 'loading_ramp', name: 'Loading ramp',
    // Driven over.
    halfX: 2.8, halfZ: 2.5, height: 1.55, modelScale: 5.7, modelYaw: Math.PI / 2,
    model: MESH('loading-ramp'),
    blocksTanks: false, blocksShells: false,
  }),
  slab_stack: prop({
    id: 'slab_stack', name: 'Slab stack',
    halfX: 1.81, halfZ: 1.8, height: 2.74, modelScale: 3.68, modelYaw: 0,
    model: MESH('slab-stack'),
  }),

  // ── Infrastructure ──
  pylon_concrete: prop({
    id: 'pylon_concrete', name: 'Concrete portal',
    halfX: 2.2, halfZ: 0.48, height: 6.29, modelScale: 6.42, modelYaw: Math.PI / 2,
    model: MESH('pylon-concrete'),
  }),
  utility_pole: prop({
    id: 'utility_pole', name: 'Utility pole',
    halfX: 0.35, halfZ: 0.35, height: 8, modelScale: 8.17, modelYaw: 0,
    model: MESH('utility-pole'),
  }),
  fuel_tank_horizontal: prop({
    id: 'fuel_tank_horizontal', name: 'Fuel tank',
    halfX: 3.16, halfZ: 2.2, height: 5.44, modelScale: 6.44, modelYaw: Math.PI / 2,
    model: MESH('fuel-tank-horizontal'),
  }),
  silo_battery: prop({
    id: 'silo_battery', name: 'Silo battery',
    halfX: 5, halfZ: 1.82, height: 9.52, modelScale: 10.21, modelYaw: Math.PI / 2,
    model: MESH('silo-battery'),
  }),
  bridge_stone: prop({
    id: 'bridge_stone', name: 'Arch bridge',
    halfX: 3.99, halfZ: 3, height: 3.14, modelScale: 8.13, modelYaw: Math.PI / 2,
    model: MESH('bridge-stone'),
  }),
  culvert_headwall: prop({
    id: 'culvert_headwall', name: 'Culvert headwall',
    halfX: 1.47, halfZ: 1.2, height: 1.82, modelScale: 2.98, modelYaw: Math.PI / 2,
    model: MESH('culvert-headwall'),
  }),
  cargo_container: prop({
    id: 'cargo_container', name: 'Cargo container',
    halfX: 3.05, halfZ: 1.74, height: 3.67, modelScale: 6.24, modelYaw: 0,
    model: MESH('cargo-container'),
  }),
  container_stack: prop({
    id: 'container_stack', name: 'Container stack',
    halfX: 3.05, halfZ: 1.49, height: 5.58, modelScale: 6.23, modelYaw: Math.PI / 2,
    model: MESH('container-stack'),
  }),
  water_cistern: prop({
    id: 'water_cistern', name: 'Water cistern',
    halfX: 2.4, halfZ: 2.26, height: 4.95, modelScale: 5.05, modelYaw: 0,
    model: MESH('water-cistern'),
  }),

  // ── Wrecked vehicles and street furniture ──
  wreck_car: prop({
    id: 'wreck_car', name: 'Burnt-out car',
    // Re-modelled 2026-09-03 at 10,757 tris against the previous 19,086 —
    // same footprint contract as before (fits the old 3.44 x 1.8 box; width
    // is the binding constraint here, so length lands 0.02 m under it), width
    // narrower than length just as it was.
    halfX: 1.71, halfZ: 0.9, height: 1.65, modelScale: 3.49, modelYaw: Math.PI / 2,
    model: MESH('wreck-car'),
  }),
  wreck_truck: prop({
    id: 'wreck_truck', name: 'Derelict lorry',
    halfX: 2.77, halfZ: 1.3, height: 2.59, modelScale: 5.65, modelYaw: 0,
    model: MESH('wreck-truck'),
  }),
  wreck_bus: prop({
    id: 'wreck_bus', name: 'Derelict bus',
    halfX: 3.36, halfZ: 1.4, height: 2.61, modelScale: 6.85, modelYaw: Math.PI / 2,
    model: MESH('wreck-bus'),
  }),
  bus_shelter: prop({
    id: 'bus_shelter', name: 'Bus shelter',
    halfX: 2, halfZ: 1.17, height: 2.68, modelScale: 4.09, modelYaw: Math.PI / 2,
    model: MESH('bus-shelter'),
  }),
  billboard: prop({
    id: 'billboard', name: 'Billboard',
    halfX: 3, halfZ: 0.45, height: 6.1, modelScale: 6.23, modelYaw: Math.PI / 2,
    model: MESH('billboard'),
  }),
  milestone: prop({
    id: 'milestone', name: 'Milestone',
    halfX: 1.1, halfZ: 1.09, height: 2.2, modelScale: 2.25, modelYaw: Math.PI / 2,
    model: MESH('milestone'),
  }),

  // ── Vehicles that are not wrecks ──
  car_parked: prop({
    id: 'car_parked', name: 'Parked car',
    halfX: 2.04, halfZ: 0.95, height: 1.88, modelScale: 4.16, modelYaw: Math.PI / 2,
    model: MESH('car-parked'),
  }),
  lorry_loaded: prop({
    id: 'lorry_loaded', name: 'Loaded lorry',
    halfX: 2.78, halfZ: 1.2, height: 2.83, modelScale: 5.66, modelYaw: 0,
    model: MESH('lorry-loaded'),
  }),
  tractor_farm: prop({
    id: 'tractor_farm', name: 'Farm tractor',
    halfX: 1.55, halfZ: 1, height: 2.13, modelScale: 3.17, modelYaw: 0,
    model: MESH('tractor-farm'),
  }),
  field_gun: prop({
    id: 'field_gun', name: 'Field gun',
    halfX: 1.2, halfZ: 2.8, height: 3.02, modelScale: 4.74, modelYaw: 0,
    model: MESH('field-gun'),
  }),

  // ── Damaged but standing ──
  house_shelled: prop({
    id: 'house_shelled', name: 'Shelled house',
    halfX: 3.5, halfZ: 2.91, height: 7.88, modelScale: 8.06, modelYaw: 0,
    model: MESH('house-shelled'),
  }),
  terrace_shelled: prop({
    id: 'terrace_shelled', name: 'Shelled terrace',
    halfX: 7.56, halfZ: 4.34, height: 11.12, modelScale: 15.46, modelYaw: Math.PI / 2,
    model: MESH('terrace-shelled'),
  }),
  apartment_shelled: prop({
    id: 'apartment_shelled', name: 'Shelled apartment block',
    halfX: 5.57, halfZ: 3.94, height: 11.03, modelScale: 11.37, modelYaw: 0,
    model: MESH('apartment-shelled'),
  }),
  warehouse_shelled: prop({
    id: 'warehouse_shelled', name: 'Shelled warehouse',
    halfX: 10, halfZ: 5.74, height: 8.53, modelScale: 20.68, modelYaw: 0,
    model: MESH('warehouse-shelled'),
  }),

  // ── Small clutter and farm ──
  oil_drums: prop({
    id: 'oil_drums', name: 'Oil drums',
    halfX: 1.12, halfZ: 1, height: 1.24, modelScale: 2.28, modelYaw: 0,
    model: MESH('oil-drums'),
  }),
  tyre_stack: prop({
    id: 'tyre_stack', name: 'Tyre stack',
    halfX: 1, halfZ: 0.92, height: 1.88, modelScale: 2.04, modelYaw: Math.PI / 2,
    model: MESH('tyre-stack'),
  }),
  haystack: prop({
    id: 'haystack', name: 'Haystack',
    halfX: 2.5, halfZ: 2.5, height: 4.32, modelScale: 5.1, modelYaw: 0,
    model: MESH('haystack'),
  }),

  // ── Street and vegetation ──
  war_memorial: prop({
    id: 'war_memorial', name: 'War memorial',
    halfX: 1.5, halfZ: 1.5, height: 4.19, modelScale: 4.29, modelYaw: Math.PI / 2,
    model: MESH('war-memorial'),
  }),
  coppice_stand: prop({
    id: 'coppice_stand', name: 'Coppice stand',
    // Hull-height extent is the whole footprint.
    halfX: 3.67, halfZ: 3, height: 6.3, modelScale: 7.51, modelYaw: Math.PI / 2,
    model: MESH('coppice-stand'),
    blocksTanks: false, blocksShells: false,
  }),

  // ── Industrial yard dressing ──
  metal_shelf_clutter: prop({
    id: 'metal_shelf_clutter', name: 'Metal shelving',
    halfX: 0.91, halfZ: 0.4, height: 2, modelScale: 2.04, modelYaw: 0,
    model: MESH('metal-shelf-clutter'),
  }),
  tool_cabinet: prop({
    id: 'tool_cabinet', name: 'Tool cabinet',
    halfX: 0.48, halfZ: 0.36, height: 1.1, modelScale: 1.12, modelYaw: 0,
    model: MESH('tool-cabinet'),
  }),
  rusty_oil_barrels: prop({
    id: 'rusty_oil_barrels', name: 'Rusty oil barrels',
    halfX: 0.66, halfZ: 0.6, height: 0.85, modelScale: 1.34, modelYaw: 0,
    model: MESH('rusty-oil-barrels'),
  }),
  single_oil_barrel: prop({
    id: 'single_oil_barrel', name: 'Oil barrel',
    // One drum rather than a cluster, so its footprint is a real drum's
    // diameter (~0.63 m) rather than the two- and three-barrel groups above.
    halfX: 0.32, halfZ: 0.32, height: 0.85, modelScale: 0.85, modelYaw: 0,
    model: MESH('single-oil-barrel'),
  }),
  rolling_shutter: prop({
    id: 'rolling_shutter', name: 'Rolling shutter',
    halfX: 3, halfZ: 0.94, height: 5.86, modelScale: 6.12, modelYaw: 0,
    model: MESH('rolling-shutter'),
  }),
  brick_window: prop({
    id: 'brick_window', name: 'Brick window fragment',
    halfX: 0.7, halfZ: 0.23, height: 1.26, modelScale: 1.44, modelYaw: 0,
    model: MESH('brick-window'),
  }),
  industrial_crane: prop({
    id: 'industrial_crane', name: 'Industrial crane',
    halfX: 0.47, halfZ: 0.71, height: 1.8, modelScale: 1.84, modelYaw: 0,
    model: MESH('industrial-crane'),
  }),
  rusty_pillar: prop({
    id: 'rusty_pillar', name: 'Rusty support pillar',
    halfX: 0.485, halfZ: 0.485, height: 2, modelScale: 2.04, modelYaw: 0,
    model: MESH('rusty-pillar'),
  }),
  military_truck: prop({
    id: 'military_truck', name: 'Military truck',
    halfX: 1.21, halfZ: 2.77, height: 2.84, modelScale: 5.69, modelYaw: 0,
    model: MESH('military-truck'),
  }),
  off_road_vehicle: prop({
    id: 'off_road_vehicle', name: 'Off-road vehicle',
    halfX: 1.08, halfZ: 2.3, height: 2.56, modelScale: 4.602, modelYaw: 0,
    model: MESH('off-road-vehicle'),
  }),

  // ── Map editor: collision-only volumes. See `invisible` above ──
  clip_wall: prop({
    id: 'clip_wall', name: 'Clip zone — movement + shots',
    model: null,
    invisible: true,
    halfX: 3, halfZ: 0.3, height: 3,
    blocksTanks: true, blocksShells: true,
  }),
  clip_wall_move: prop({
    id: 'clip_wall_move', name: 'Clip zone — movement only',
    // The catalogue's one shell-blocking exception.
    model: null,
    invisible: true,
    halfX: 3, halfZ: 0.3, height: 3,
    blocksTanks: true, blocksShells: false,
  }),
};

export const CLIP_VOLUMES: readonly PropId[] = Object.values(PROP_DEFINITIONS)
  .filter((d) => d.invisible)
  .map((d) => d.id);

/** Looks up a definition, or `undefined` for an id nothing knows about. */
export function propDefinition(id: PropId | null | undefined): PropDefinition | undefined {
  return id ? PROP_DEFINITIONS[id] : undefined;
}

/**
 * Every prop a tank drives through, and the only ones.
 *
 * **Derived from the catalogue rather than restated**, so this cannot disagree
 * with the definitions — it is a *reading* of them, in one place, for the two
 * callers that want the family rather than the flag: `props.test.ts`, which
 * asserts the set is exactly the list below, and anybody laying out a map who
 * needs to know what will not stop a hull.
 *
 * The list it is checked against lives in the test rather than here, and that is
 * deliberate: a constant here plus a flag on each definition would be the same
 * fact in two places, and the one that goes stale is always the one nobody
 * balances against. What the test pins is the *policy* — vegetation and ground
 * features, nothing built — so adding a pass-through prop is a decision somebody
 * has to take out loud in a test diff rather than a flag they can slip into a
 * definition.
 */
export const PASS_THROUGH: readonly PropId[] = Object.values(PROP_DEFINITIONS)
  .filter((d) => !d.blocksTanks)
  .map((d) => d.id);

// ─── Geometry ────────────────────────────────────────────────────────────────
//
// All of it pure, and all of it in the ground plane — height is carried for the
// renderer but nothing here uses it, because a tank cannot climb and a shell
// flies flat enough that pretending otherwise would only add ways to disagree.

/** Rotates a world offset into a box's local frame. */
function toLocal(dx: number, dz: number, yaw: number): { lx: number; lz: number } {
  const cos = Math.cos(yaw);
  const sin = Math.sin(yaw);
  // Inverse of the Y rotation the placement applies.
  return { lx: dx * cos - dz * sin, lz: dx * sin + dz * cos };
}

/** Rotates a local offset back out into world space. */
function toWorld(lx: number, lz: number, yaw: number): { dx: number; dz: number } {
  const cos = Math.cos(yaw);
  const sin = Math.sin(yaw);
  return { dx: lx * cos + lz * sin, dz: -lx * sin + lz * cos };
}

/**
 * Metres a resolved circle is pushed past contact.
 *
 * Small enough to be invisible — a tank is five and a half metres long — and
 * large enough to break the tie between two boxes that share an edge.
 */
const CONTACT_SKIN = 0.005;

export interface Pushout {
  /** Unit vector along which the circle must move, in world space. */
  nx: number;
  nz: number;
  /** How far it must move, in metres. */
  depth: number;
}

/**
 * Resolves a circle overlapping an oriented box.
 *
 * Returns the shortest push that separates them, or `null` if they are apart.
 * The circle is the tank; the box is the building.
 *
 * A circle entirely *inside* the box is the awkward case — there is no closest
 * surface point to push away from — and it is handled by ejecting through the
 * nearest face. It should not arise, but a tank that spawns inside a wall must
 * come out rather than stick.
 */
export function circleVsBox(
  cx: number,
  cz: number,
  radius: number,
  body: Pick<PropBody, 'x' | 'z' | 'yaw' | 'halfX' | 'halfZ'>,
): Pushout | null {
  const { lx, lz } = toLocal(cx - body.x, cz - body.z, body.yaw);

  const clampedX = Math.max(-body.halfX, Math.min(body.halfX, lx));
  const clampedZ = Math.max(-body.halfZ, Math.min(body.halfZ, lz));
  const dx = lx - clampedX;
  const dz = lz - clampedZ;
  const distanceSq = dx * dx + dz * dz;

  if (distanceSq > radius * radius) return null;

  if (distanceSq > 1e-12) {
    // Outside the box, overlapping the closest surface point.
    const distance = Math.sqrt(distanceSq);
    const depth = radius - distance;
    const world = toWorld(dx / distance, dz / distance, body.yaw);
    return { nx: world.dx, nz: world.dz, depth };
  }

  // Centre is inside: leave through whichever face is nearest.
  const toRight = body.halfX - lx;
  const toLeft = body.halfX + lx;
  const toFront = body.halfZ - lz;
  const toBack = body.halfZ + lz;
  const smallest = Math.min(toRight, toLeft, toFront, toBack);

  let localX = 0;
  let localZ = 0;
  if (smallest === toRight) localX = 1;
  else if (smallest === toLeft) localX = -1;
  else if (smallest === toFront) localZ = 1;
  else localZ = -1;

  const world = toWorld(localX, localZ, body.yaw);
  return { nx: world.dx, nz: world.dz, depth: smallest + radius };
}

/**
 * Slides a circle out of every body it overlaps.
 *
 * Iterated, because pushing out of one wall can push into another — a tank in
 * an inside corner has to satisfy both. A handful of passes settles it; the cap
 * exists so a tank wedged into geometry that cannot be satisfied costs a bounded
 * amount of work rather than hanging the tick.
 */
export function resolveCircle(
  cx: number,
  cz: number,
  radius: number,
  bodies: readonly PropBody[],
  passes = 4,
): { x: number; z: number; hit: boolean } {
  let x = cx;
  let z = cz;
  let hit = false;

  for (let pass = 0; pass < passes; pass += 1) {
    let moved = false;
    for (const body of bodies) {
      if (!body.blocksTanks) continue;
      // Cheap rejection first: most props are nowhere near.
      const reach = radius + body.boundingRadius;
      const dx = x - body.x;
      const dz = z - body.z;
      if (dx * dx + dz * dz > reach * reach) continue;

      const push = circleVsBox(x, z, radius, body);
      if (!push) continue;
      // A hair beyond contact rather than exactly onto it. Resting precisely on
      // the surface leaves the circle still touching every neighbouring box, so
      // in a run of wall sections each pass hands a residual overlap to the next
      // and the loop gives up a millimetre or two inside. Separating properly
      // lets it converge.
      const depth = push.depth + CONTACT_SKIN;
      x += push.nx * depth;
      z += push.nz * depth;
      moved = true;
      hit = true;
    }
    if (!moved) break;
  }

  return { x, z, hit };
}

/**
 * Distance along a segment at which it first enters a box, or `null`.
 *
 * The slab method, in the box's local frame. Used for shells: a round that would
 * pass through a building this tick stops at the wall instead.
 *
 * Returned as a fraction of the segment, 0–1, so the caller can place the impact
 * without repeating the arithmetic.
 */
export function segmentVsBox(
  x0: number,
  z0: number,
  x1: number,
  z1: number,
  body: Pick<PropBody, 'x' | 'z' | 'yaw' | 'halfX' | 'halfZ'>,
): number | null {
  const start = toLocal(x0 - body.x, z0 - body.z, body.yaw);
  const end = toLocal(x1 - body.x, z1 - body.z, body.yaw);
  const dx = end.lx - start.lx;
  const dz = end.lz - start.lz;

  let tMin = 0;
  let tMax = 1;

  // One slab per axis, intersected.
  const axes: Array<[number, number, number]> = [
    [start.lx, dx, body.halfX],
    [start.lz, dz, body.halfZ],
  ];

  for (const [origin, delta, half] of axes) {
    if (Math.abs(delta) < 1e-12) {
      // Parallel to this slab: either inside it for the whole segment, or never.
      if (origin < -half || origin > half) return null;
      continue;
    }
    const inverse = 1 / delta;
    let near = (-half - origin) * inverse;
    let far = (half - origin) * inverse;
    if (near > far) [near, far] = [far, near];
    if (near > tMin) tMin = near;
    if (far < tMax) tMax = far;
    if (tMin > tMax) return null;
  }

  return tMin;
}

/** Whether a point is inside a box's footprint. */
export function pointInBox(
  x: number,
  z: number,
  body: Pick<PropBody, 'x' | 'z' | 'yaw' | 'halfX' | 'halfZ'>,
): boolean {
  const local = toLocal(x - body.x, z - body.z, body.yaw);
  return Math.abs(local.lx) <= body.halfX && Math.abs(local.lz) <= body.halfZ;
}

/**
 * The first prop a segment **enters**, or `null` if it reaches the far end.
 *
 * Nearest wins, so a shell fired through a row of buildings stops at the first
 * rather than at whichever happened to be listed first.
 *
 * **A prop the segment starts inside is not entered, and does not block.** That
 * is not a detail: two props in the catalogue stop shells and let tanks through
 * — the sandbag wall, whose whole purpose is hull-down cover a tank grinds onto,
 * and the hedgerow. A tank parked on either has its muzzle inside a
 * shell-blocking box, because `MUZZLE_OFFSET` is 2.05 m and nothing keeps a hull
 * out of a prop it is allowed to drive over. `segmentVsBox` reports 0 for a
 * segment that starts inside, so every round that player fired died at its own
 * muzzle: no impact, no damage, and a client-side tracer that flew on and looked
 * like a hit. It was reported as "shots are not registered though he did not
 * miss", from the one piece of cover the map invites you to sit in.
 *
 * Modelling it the other way — a round detonating on the inside face of the
 * sandbags it was fired from — is not the more physical answer either. The round
 * is already past that surface.
 *
 * `commands.ts` asks the same question for a bot's line of sight, and wants the
 * same answer for the same reason: a unit sitting in a hedge can shoot out of
 * it. Keeping the rule here rather than at each call site is what stops the two
 * disagreeing about whether a shot exists.
 */
export function firstBlockingProp(
  x0: number,
  z0: number,
  x1: number,
  z1: number,
  bodies: readonly PropBody[],
): { body: PropBody; t: number } | null {
  let best: { body: PropBody; t: number } | null = null;
  for (const body of bodies) {
    if (!body.blocksShells) continue;
    // Cheap reject first: a body whose bounding circle the segment does not
    // even pass near cannot possibly block it, and this test is one dot
    // product with no trig and no allocation — a few arithmetic ops against
    // `segmentVsBox`'s two axis-aligned transforms. It matters at scale: a
    // densely dressed map can carry several hundred shell-blocking props, and
    // this is asked for every shot *and* every bot's line-of-sight check,
    // every tick. See `segmentNearCircle`.
    if (!segmentNearCircle(x0, z0, x1, z1, body.x, body.z, body.boundingRadius)) continue;
    if (pointInBox(x0, z0, body)) continue;
    const t = segmentVsBox(x0, z0, x1, z1, body);
    if (t === null) continue;
    if (!best || t < best.t) best = { body, t };
  }
  return best;
}

/**
 * Cheap broad-phase reject: could this segment possibly pass within `radius`
 * of `(cx, cz)`?
 *
 * No trig, no allocation — the closest point on the segment to the circle's
 * centre, clamped to the segment's own length, compared by squared distance.
 * A `false` here is exact (the segment truly cannot reach the circle); a
 * `true` is only "worth checking properly" — `segmentVsBox`'s oriented test
 * still runs afterward and is the one that can say no.
 */
export function segmentNearCircle(
  x0: number,
  z0: number,
  x1: number,
  z1: number,
  cx: number,
  cz: number,
  radius: number,
): boolean {
  const dx = x1 - x0;
  const dz = z1 - z0;
  const lengthSq = dx * dx + dz * dz;
  const t =
    lengthSq > 1e-9 ? Math.max(0, Math.min(1, ((cx - x0) * dx + (cz - z0) * dz) / lengthSq)) : 0;
  const px = x0 + dx * t;
  const pz = z0 + dz * t;
  const clearance = px - cx;
  const clearanceZ = pz - cz;
  return clearance * clearance + clearanceZ * clearanceZ <= radius * radius;
}
